// src/components/Programs.tsx
import React, { useState, useEffect } from 'react';
import styles from '../styles/ProgrammesPage.module.css';

const Programs: React.FC = () => {
  const [activeDegree, setActiveDegree] = useState<'undergraduate'|'graduate'|'phd'|'certificates'>('undergraduate');

  // Scroll to section when component mounts or when activeDegree changes
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          
          // If it's a degree section, activate the corresponding tab
          if (['undergraduate', 'graduate', 'phd', 'certificates'].includes(sectionId)) {
            setActiveDegree(sectionId as any);
          }
        }, 100);
      }
    }
  }, []);

  // Handle tab click and update URL hash
  const handleTabClick = (degree: 'undergraduate'|'graduate'|'phd'|'certificates') => {
    setActiveDegree(degree);
    window.history.replaceState(null, '', `#${degree}`);
    const element = document.getElementById(degree);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <div className={styles['philosophy-program']}>

      {/* Header */}
      <header className={styles['program-header']}>
        <div className={styles['header-content']}>
          <h1>Philosophy Programs</h1>
          <p>Explore wisdom, develop critical thinking, and transform your worldview</p>
        </div>
      </header>

      <main className={styles['program-content']}>

        {/* Programs Offered */}
        <section id="academic-programs" className={styles['program-section']}>
          <div className={styles['section-header']}>
            <h2 className={styles['section-title']}>Our Academic Programs</h2>
            <p className={styles['section-intro']}>
              Explore our diverse range of philosophy programs designed to cultivate critical thinking,
              ethical reasoning, and deep understanding of philosophical traditions.
            </p>
          </div>

          {/* Degree Sub-Tabs */}
          <div className={styles['degree-tabs']}>
            <button
              id="undergraduate"
              className={`${styles['degree-tab']} ${activeDegree === 'undergraduate' ? styles.active : ''}`}
              onClick={() => handleTabClick('undergraduate')}
            >
              Undergraduate
            </button>
            <button
              id="graduate"
              className={`${styles['degree-tab']} ${activeDegree === 'graduate' ? styles.active : ''}`}
              onClick={() => handleTabClick('graduate')}
            >
              Graduate
            </button>
            <button
              id="phd"
              className={`${styles['degree-tab']} ${activeDegree === 'phd' ? styles.active : ''}`}
              onClick={() => handleTabClick('phd')}
            >
              Doctoral
            </button>
            <button
              id="certificates"
              className={`${styles['degree-tab']} ${activeDegree === 'certificates' ? styles.active : ''}`}
              onClick={() => handleTabClick('certificates')}
            >
              Certificates
            </button>
          </div>

          <div className={styles['programs-container']}>

            {/* Undergraduate */}
            {activeDegree === 'undergraduate' && (
              <div className={styles['program-grid']}>
                <div className={styles['program-card']}>
                  <div className={styles['program-icon']}>
                    <img src="https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Bachelor of Arts" />
                  </div>
                  <div className={styles['program-details']}>
                    <h3>Bachelor of Arts in Philosophy</h3>
                    <p className={styles['program-desc']}>
                      Comprehensive study of philosophical traditions, critical thinking, and ethical reasoning.
                      Prepares students for diverse careers through rigorous intellectual training.
                    </p>
                    <div className={styles['program-meta']}>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Duration:</span>
                        <span className={styles['meta-value']}>4 years</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Credits:</span>
                        <span className={styles['meta-value']}>120</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Format:</span>
                        <span className={styles['meta-value']}>Full-time/Part-time</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles['program-card']}>
                  <div className={styles['program-icon']}>
                    <img src="https://images.unsplash.com/photo-1600431521340-491eca880813?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1169&q=80" 
                         alt="Philosophy & Cognitive Science" />
                  </div>
                  <div className={styles['program-details']}>
                    <h3>Philosophy & Cognitive Science</h3>
                    <p className={styles['program-desc']}>
                      Interdisciplinary program exploring the mind, consciousness, and artificial intelligence
                      through philosophical and scientific perspectives.
                    </p>
                    <div className={styles['program-meta']}>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Duration:</span>
                        <span className={styles['meta-value']}>4 years</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Credits:</span>
                        <span className={styles['meta-value']}>124</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Format:</span>
                        <span className={styles['meta-value']}>Full-time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Graduate */}
            {activeDegree === 'graduate' && (
              <div className={styles['program-grid']}>
                <div className={styles['program-card']}>
                  <div className={styles['program-icon']}>
                    <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Master of Arts" />
                  </div>
                  <div className={styles['program-details']}>
                    <h3>Master of Arts in Philosophy</h3>
                    <p className={styles['program-desc']}>
                      Advanced study in specialized areas of philosophy with opportunities for original research.
                      Prepares students for doctoral studies or professional careers.
                    </p>
                    <div className={styles['program-meta']}>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Duration:</span>
                        <span className={styles['meta-value']}>2 years</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Credits:</span>
                        <span className={styles['meta-value']}>36</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Format:</span>
                        <span className={styles['meta-value']}>Full-time/Part-time</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles['program-card']}>
                  <div className={styles['program-icon']}>
                    <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Ethics & Public Policy" />
                  </div>
                  <div className={styles['program-details']}>
                    <h3>Ethics & Public Policy</h3>
                    <p className={styles['program-desc']}>
                      Focused program examining ethical dimensions of policy decisions in government,
                      healthcare, and technology sectors.
                    </p>
                    <div className={styles['program-meta']}>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Duration:</span>
                        <span className={styles['meta-value']}>18 months</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Credits:</span>
                        <span className={styles['meta-value']}>33</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Format:</span>
                        <span className={styles['meta-value']}>Full-time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Doctoral */}
            {activeDegree === 'phd' && (
              <div className={styles['program-grid']}>
                <div className={styles['program-card']}>
                  <div className={styles['program-icon']}>
                    <img src="https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Doctor of Philosophy" />
                  </div>
                  <div className={styles['program-details']}>
                    <h3>Doctor of Philosophy</h3>
                    <p className={styles['program-desc']}>
                      Premier research program for students pursuing academic careers or advanced research positions.
                      Specializations available in various philosophical traditions.
                    </p>
                    <div className={styles['program-meta']}>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Duration:</span>
                        <span className={styles['meta-value']}>4-6 years</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Credits:</span>
                        <span className={styles['meta-value']}>72</span>
                      </div>
                      <div className={styles['meta-item']}>
                        <span className={styles['meta-label']}>Format:</span>
                        <span className={styles['meta-value']}>Full-time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Certificates */}
            {activeDegree === 'certificates' && (
              <div className={styles['certificate-grid']}>
                <div className={styles['certificate-card']}>
                  <div className={styles['certificate-image']}>
                    <img src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Applied Ethics" />
                  </div>
                  <h3>Applied Ethics</h3>
                  <p>Specialized training in ethical decision-making for professional contexts</p>
                  <div className={styles['cert-meta']}>
                    <span>12 credits</span>
                    <span>6 months</span>
                  </div>
                  <button className={styles['cert-button']}>View Details</button>
                </div>
                <div className={styles['certificate-card']}>
                  <div className={styles['certificate-image']}>
                    <img src="https://images.unsplash.com/photo-1586773860418-d37222d8fce3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1173&q=80" 
                         alt="Philosophy of Science" />
                  </div>
                  <h3>Philosophy of Science</h3>
                  <p>Examine foundational questions in scientific methodology and knowledge</p>
                  <div className={styles['cert-meta']}>
                    <span>9 credits</span>
                    <span>4 months</span>
                  </div>
                  <button className={styles['cert-button']}>View Details</button>
                </div>
                <div className={styles['certificate-card']}>
                  <div className={styles['certificate-image']}>
                    <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="Digital Ethics" />
                  </div>
                  <h3>Digital Ethics</h3>
                  <p>Ethical frameworks for technology, AI, and digital society</p>
                  <div className={styles['cert-meta']}>
                    <span>12 credits</span>
                    <span>6 months</span>
                  </div>
                  <button className={styles['cert-button']}>View Details</button>
                </div>
                <div className={styles['certificate-card']}>
                  <div className={styles['certificate-image']}>
                    <img src="https://images.unsplash.com/photo-1619983081593-e2ba5b543168?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                         alt="African Philosophy" />
                  </div>
                  <h3>African Philosophy</h3>
                  <p>Exploration of philosophical traditions across the African continent</p>
                  <div className={styles['cert-meta']}>
                    <span>9 credits</span>
                    <span>4 months</span>
                  </div>
                  <button className={styles['cert-button']}>View Details</button>
                </div>
              </div>
            )}

          </div>
        </section>

        {/* Admission Process */}
        <section id="admission" className={styles['program-section']}>
          <div className={styles['section-header']}>
            <h2 className={styles['section-title']}>Admission Process</h2>
            <p className={styles['section-intro']}>
              Our admission process is designed to identify students who demonstrate intellectual curiosity
              and potential for philosophical inquiry.
            </p>
          </div>

          <div className={styles['process-steps']}>
            {[
              {
                num: '1',
                title: 'Application Submission',
                desc: 'Complete online application form with personal and academic information',
                details: ['Deadlines: Fall: May 15, Spring: Oct 30', 'Fee: $75 (waivers available)'],
                icon: '📝'
              },
              {
                num: '2',
                title: 'Documentation',
                desc: 'Submit required documents including transcripts and test scores',
                details: ['Requirements: Official transcripts, Letters of recommendation', 'Graduate: Writing sample, Statement of purpose'],
                icon: '📄'
              },
              {
                num: '3',
                title: 'Review Process',
                desc: 'Faculty committee evaluates applications holistically',
                details: ['Timeline: 4-6 weeks after deadline', 'Criteria: Academic record, writing ability, philosophical potential'],
                icon: '🔍'
              },
              {
                num: '4',
                title: 'Decision & Enrollment',
                desc: 'Receive admission decision and complete enrollment',
                details: ['Notification: Via email and portal', 'Next Steps: Submit enrollment deposit, attend orientation'],
                icon: '🎉'
              }
            ].map(step => (
              <div key={step.num} className={styles['step-card']}>
                <div className={styles['step-icon']}>{step.icon}</div>
                <div className={styles['step-number']}>{step.num}</div>
                <div className={styles['step-content']}>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                  <div className={styles['step-details']}>
                    {step.details.map((d, i) => <p key={i}><strong>{d.split(':')[0]}:</strong> {d.split(': ')[1]}</p>)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles['admission-timeline']}>
            <div className={styles['timeline-image']}>
              <img src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                   alt="Admission Timeline" />
            </div>
            <div className={styles['timeline-content-container']}>
              <h3 className={styles['timeline-title']}>Application Timeline</h3>
              <div className={styles['timeline-container']}>
                {[
                  { date: 'Aug - Oct', title: 'Early Application Period', desc: 'Priority consideration for scholarships and assistantships' },
                  { date: 'Nov - Jan', title: 'Regular Decision Period', desc: 'Standard application review for all programs' },
                  { date: 'Feb - Apr', title: 'Admission Decisions', desc: 'Notifications sent to all applicants' },
                  { date: 'May - Jun', title: 'Enrollment & Orientation', desc: 'Welcome events for new students' }
                ].map((item, idx) => (
                  <div key={idx} className={styles['timeline-item']}>
                    <div className={styles['timeline-date']}>{item.date}</div>
                    <div className={styles['timeline-content']}>
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section id="requirements" className={styles['program-section']}>
          <div className={styles['section-header']}>
            <h2 className={styles['section-title']}>Admission Requirements</h2>
            <p className={styles['section-intro']}>
              Below are the admission requirements for our various programs. International students should
              review the additional requirements section.
            </p>
          </div>

          <div className={styles['requirements-grid']}>
            {[
              {
                title: 'Undergraduate Programs',
                items: [
                  'High school diploma or equivalent',
                  'Minimum GPA: 3.0 on 4.0 scale',
                  'SAT score: 1150+ or ACT: 24+',
                  'Personal essay demonstrating critical thinking',
                  'Two letters of recommendation'
                ],
                icon: '🎓'
              },
              {
                title: 'Graduate Programs',
                items: [
                  "Bachelor's degree in philosophy or related field",
                  'Minimum GPA: 3.3 in major coursework',
                  'GRE General Test (optional)',
                  'Writing sample (10-15 pages)',
                  'Statement of purpose',
                  'Three letters of recommendation'
                ],
                icon: '📚'
              },
              {
                title: 'Doctoral Program',
                items: [
                  "Master's degree in philosophy",
                  'Minimum GPA: 3.7 in graduate work',
                  'Research proposal',
                  'Writing sample (20-30 pages)',
                  'Statement of research interests',
                  'Three letters of recommendation'
                ],
                icon: '🔬'
              },
              {
                title: 'International Students',
                items: [
                  'TOEFL: 90+ or IELTS: 6.5+',
                  'Transcript evaluation (for non-US degrees)',
                  'Proof of financial support',
                  'Visa documentation'
                ],
                icon: '🌍'
              }
            ].map((req, i) => (
              <div key={i} className={styles['requirement-card']}>
                <div className={styles['requirement-icon']}>{req.icon}</div>
                <h3>{req.title}</h3>
                <ul>
                  {req.items.map((it, j) => <li key={j}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className={styles['scholarship-section']}>
            <div className={styles['scholarship-header']}>
              <h3 className={styles['scholarship-title']}>Scholarships & Financial Aid</h3>
              <p className={styles['scholarship-subtitle']}>Funding opportunities for our philosophy students</p>
            </div>
            <div className={styles['scholarship-grid']}>
              {[
                { title: 'Philosophy Merit Scholarship', desc: 'Up to $10,000/year for outstanding undergraduate applicants' },
                { title: 'Graduate Teaching Assistantship', desc: 'Full tuition waiver + $18,000 stipend for qualified graduate students' },
                { title: 'Research Fellowship', desc: '$5,000-$15,000 for doctoral research projects' },
                { title: 'Diversity Scholarship', desc: '$7,500/year for students from underrepresented backgrounds' },
                { title: 'Study Abroad Grant', desc: '$3,000 for international philosophy research' },
                { title: 'Ethics in Practice Award', desc: '$2,500 for projects applying philosophy to real-world issues' }
              ].map((sch, i) => (
                <div key={i} className={styles['scholarship-card']}>
                  <h4>{sch.title}</h4>
                  <p>{sch.desc}</p>
                  <button className={styles['scholarship-button']}>Apply Now</button>
                </div>
              ))}
            </div>
            <div className={styles['scholarship-cta']}>
              <button className={styles['view-all-button']}>View All Funding Opportunities</button>
            </div>
          </div>
        </section>

        {/* Faculty & Resources */}
        <section id="faculty-resources" className={styles['program-section']}>
          <div className={styles['section-header']}>
            <h2 className={styles['section-title']}>Faculty & Resources</h2>
            <p className={styles['section-intro']}>
              Our distinguished faculty and comprehensive resources create an environment
              conducive to deep philosophical inquiry.
            </p>
          </div>

          <div className={styles['faculty-grid']}>
            {[
              { 
                name: 'Prof. Adaobi Nnamdi', 
                title: 'Chair of Philosophy Department', 
                expertise: 'African Philosophy, Ethics', 
                email: 'an@unical.edu', 
                office: 'PHIL 305',
                image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=688&q=80'
              },
              { 
                name: 'Dr. Chike Anya', 
                title: 'Associate Professor', 
                expertise: 'Philosophy of Mind, Logic', 
                email: 'ca@unical.edu', 
                office: 'PHIL 210',
                image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80'
              },
              { 
                name: 'Dr. Fatima Aliyu', 
                title: 'Assistant Professor', 
                expertise: 'Political Philosophy, Feminist Theory', 
                email: 'fa@unical.edu', 
                office: 'PHIL 118',
                image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=688&q=80'
              },
              { 
                name: 'Dr. Emeka Obi', 
                title: 'Senior Lecturer', 
                expertise: 'Philosophy of Science, Epistemology', 
                email: 'eo@unical.edu', 
                office: 'PHIL 222',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80'
              }
            ].map((fac, i) => (
              <div key={i} className={styles['faculty-card']}>
                <div className={styles['faculty-image']} style={{ backgroundImage: `url(${fac.image})` }}></div>
                <div className={styles['faculty-info']}>
                  <h3>{fac.name}</h3>
                  <p className={styles['faculty-title']}>{fac.title}</p>
                  <p className={styles['faculty-expertise']}>{fac.expertise}</p>
                  <div className={styles['faculty-contact']}>
                    <span>{fac.email}</span>
                    <span>Office: {fac.office}</span>
                  </div>
                  <div className={styles['faculty-actions']}>
                    <button className={styles['faculty-button']}>View Profile</button>
                    <button className={`${styles['faculty-button']} ${styles.secondary}`}>Contact</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles['view-all-container']}>
            <button className={styles['view-all-button']}>View All Faculty Members</button>
          </div>

          <div className={styles['resources-section']}>
            <h3 className={styles['resources-title']}>Department Resources</h3>
            <p className={styles['resources-subtitle']}>Explore our facilities and academic support services</p>
            <div className={styles['resources-grid']}>
              {[
                { 
                  icon: '📚', 
                  title: 'Philosophy Library', 
                  desc: 'Specialized collection of 15,000+ volumes and digital resources',
                  image: 'https://images.unsplash.com/photo-1495640388908-05fa85288e61?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80'
                },
                { 
                  icon: '💻', 
                  title: 'Digital Ethics Lab', 
                  desc: 'Research space for technology ethics and AI philosophy',
                  image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80'
                },
                { 
                  icon: '🌍', 
                  title: 'Global Philosophy Center', 
                  desc: 'Hub for cross-cultural philosophical dialogue and research',
                  image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80'
                },
                { 
                  icon: '🗣️', 
                  title: 'Philosophy Colloquium', 
                  desc: 'Weekly talks by distinguished philosophers from around the world',
                  image: 'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80'
                }
              ].map((res, i) => (
                <div key={i} className={styles['resource-card']}>
                  <div className={styles['resource-image']} style={{ backgroundImage: `url(${res.image})` }}></div>
                  <div className={styles['resource-icon']}>{res.icon}</div>
                  <div className={styles['resource-info']}>
                    <h4>{res.title}</h4>
                    <p>{res.desc}</p>
                  </div>
                  <button className={styles['resource-button']}>Explore</button>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      {/* Call To Action */}
      <section className={styles['program-cta']}>
        <div className={styles['cta-content']}>
          <h2>Begin Your Philosophical Journey</h2>
          <p>Join our community of thinkers exploring life's deepest questions</p>
          <div className={styles['cta-buttons']}>
            <button className={`${styles['cta-button']} ${styles.primary}`}>Apply Now</button>
            <button className={`${styles['cta-button']} ${styles.secondary}`}>Request Information</button>
            <button className={`${styles['cta-button']} ${styles.secondary}`}>Schedule a Tour</button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Programs;